import { ExportColumnOptions } from './types';

export const DATASET_EXPORT_FILE_PREFIX = 'dataset-export';

export const DEFAULT_EXPORT_COLUMN_OPTIONS: ExportColumnOptions = {
  roundingMode: 'none',
  decimalPlaces: '',
  maxLength: '',
  prefix: '',
  suffix: '',
};

export const XLSX = 'xlsx';
export const CSV = 'csv';
export const TXT = 'txt';
export const XML = 'xml';
export const JSON_FORMAT = 'json';

// The JSON serializer injects this key per master row, so a same-named column would collide.
export const JSON_SUBSETS_KEY = 'subsets';

export const COMMA = 'comma';
export const SEMICOLON = 'semicolon';
export const TAB = 'tab';
export const CUSTOM = 'custom';

export const FALLBACK_YIELD_EVERY_N_ROWS = 200;

// A delimiter longer than one code point cannot survive the pre-encoding quoting decision.
export const MAX_CUSTOM_DELIMITER_LENGTH = 1;

// CR/LF break the line join in every delimited writer; a quote collides with the RFC 4180 quoting CSV and TXT share.
export const FORBIDDEN_DELIMITER_CHARS_REGEX = /[\r\n]/;
export const FORBIDDEN_QUOTED_DELIMITER_CHARS_REGEX = /["\r\n]/;

// Past 15 places a double carries no extra precision, and 10 ** 309 overflows to Infinity.
export const MAX_EXPORT_DECIMAL_PLACES = 15;

export const WINDOWS_1250_CHARS: Record<string, number> = {
  '€': 0x80,
  '‚': 0x82,
  '„': 0x84,
  '…': 0x85,
  '†': 0x86,
  '‡': 0x87,
  '‰': 0x89,
  Š: 0x8a,
  '‹': 0x8b,
  Ś: 0x8c,
  Ť: 0x8d,
  Ž: 0x8e,
  Ź: 0x8f,
  '‘': 0x91,
  '’': 0x92,
  '“': 0x93,
  '”': 0x94,
  '•': 0x95,
  '–': 0x96,
  '—': 0x97,
  '™': 0x99,
  š: 0x9a,
  '›': 0x9b,
  ś: 0x9c,
  ť: 0x9d,
  ž: 0x9e,
  ź: 0x9f,
  '\u00a0': 0xa0, // NBSP
  ˇ: 0xa1,
  '˘': 0xa2,
  Ł: 0xa3,
  '¤': 0xa4,
  Ą: 0xa5,
  '¦': 0xa6,
  '§': 0xa7,
  '¨': 0xa8,
  '©': 0xa9,
  Ş: 0xaa,
  '«': 0xab,
  '¬': 0xac,
  '\u00ad': 0xad, // soft hyphen
  '®': 0xae,
  Ż: 0xaf,
  '°': 0xb0,
  '±': 0xb1,
  '˛': 0xb2,
  ł: 0xb3,
  '´': 0xb4,
  µ: 0xb5,
  '¶': 0xb6,
  '·': 0xb7,
  '¸': 0xb8,
  ą: 0xb9,
  ş: 0xba,
  '»': 0xbb,
  Ľ: 0xbc,
  '˝': 0xbd,
  ľ: 0xbe,
  ż: 0xbf,
  Ŕ: 0xc0,
  Á: 0xc1,
  Â: 0xc2,
  Ă: 0xc3,
  Ä: 0xc4,
  Ĺ: 0xc5,
  Ć: 0xc6,
  Ç: 0xc7,
  Č: 0xc8,
  É: 0xc9,
  Ę: 0xca,
  Ë: 0xcb,
  Ě: 0xcc,
  Í: 0xcd,
  Î: 0xce,
  Ď: 0xcf,
  Đ: 0xd0,
  Ń: 0xd1,
  Ň: 0xd2,
  Ó: 0xd3,
  Ô: 0xd4,
  Ő: 0xd5,
  Ö: 0xd6,
  '×': 0xd7,
  Ř: 0xd8,
  Ů: 0xd9,
  Ú: 0xda,
  Ű: 0xdb,
  Ü: 0xdc,
  Ý: 0xdd,
  Ţ: 0xde,
  ß: 0xdf,
  ŕ: 0xe0,
  á: 0xe1,
  â: 0xe2,
  ă: 0xe3,
  ä: 0xe4,
  ĺ: 0xe5,
  ć: 0xe6,
  ç: 0xe7,
  č: 0xe8,
  é: 0xe9,
  ę: 0xea,
  ë: 0xeb,
  ě: 0xec,
  í: 0xed,
  î: 0xee,
  ď: 0xef,
  đ: 0xf0,
  ń: 0xf1,
  ň: 0xf2,
  ó: 0xf3,
  ô: 0xf4,
  ő: 0xf5,
  ö: 0xf6,
  '÷': 0xf7,
  ř: 0xf8,
  ů: 0xf9,
  ú: 0xfa,
  ű: 0xfb,
  ü: 0xfc,
  ý: 0xfd,
  ţ: 0xfe,
  '˙': 0xff,
};

export const XML_NAME_REPLACEMENT = '_';
export const XML_NAME_FALLBACK = 'column';

export const INVALID_XML_NAME_CHARS_REGEX = /[^A-Za-z0-9_.-]/g;
export const VALID_XML_NAME_START_REGEX = /^[A-Za-z_]/;

// Full-width variants are listed by OWASP alongside the ASCII four.
export const FORMULA_TRIGGER_CHARS = [
  '=',
  '+',
  '-',
  '@',
  '＝',
  '＋',
  '－',
  '＠',
];
export const FORMULA_ESCAPE_PREFIX = "'";
export const LEADING_WHITESPACE_REGEX = /^\s+/;
export const WHITESPACE_REGEX = /\s/g;
export const SIGNED_NUMBER_REGEX = /^[+-]?\d+(?:[.,]\d+)?$/;
